import type { WelfareFishCandidate } from '@scaleaq/sitecontrol-lib';

import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { colors, responsive } from '@/utils';

import { ObservationBreakdownLine } from './fishObservationCaption/ObservationBreakdownLine.tsx';
import { liceFilterClasses, welfareFilterClasses } from './fishObservationGallery.constants.ts';
import { formatCaptured, formatMeasurement } from './fishObservationGallery.functions.ts';

type FishObservationCaptionProps = {
  observation: WelfareFishCandidate;
};

/** Metadata bar shown under the active lightbox slide. */
export const FishObservationCaption = ({ observation }: FishObservationCaptionProps) => {
  const { t, i18n } = useTranslation('orbitOne');
  const language = i18n.language;

  return (
    <FlexAligner
      alignment='bottom-left'
      direction='column'
      gap={responsive.spacing.xlarge}
      style={{
        position: 'absolute',
        inset: 'auto 0 0',
        padding: responsive.spacing['3xlarge'],
        // The lightbox backdrop stays dark in both themes, so the caption text must not follow the theme.
        color: colors.palette.white['900'],
      }}
    >
      <Typography type='body_xlarge' color='inherit'>
        {t('liceCount.imageGallery.metadata.capturedWithValue', {
          value: formatCaptured({ recordedAt: observation.recordedAt, language }),
        })}
      </Typography>
      <ObservationBreakdownLine
        label={t('liceCount.imageGallery.metadata.liceObservations')}
        observation={observation}
        filterClasses={liceFilterClasses}
      />
      <ObservationBreakdownLine
        label={t('liceCount.imageGallery.metadata.welfareObservations')}
        observation={observation}
        filterClasses={welfareFilterClasses}
      />
      <FlexAligner gap={responsive.spacing['2xlarge']}>
        <Typography type='body_xlarge' color='inherit'>
          {t('liceCount.imageGallery.metadata.weightWithValue', {
            value: formatMeasurement({
              value: observation.fish.mass,
              unit: 'kilogram',
              language,
            }),
          })}
        </Typography>
        <Typography type='body_xlarge' color='inherit'>
          {t('liceCount.imageGallery.metadata.depthWithValue', {
            value: formatMeasurement({
              value: observation.environment.depth,
              unit: 'meter',
              language,
            }),
          })}
        </Typography>
      </FlexAligner>
    </FlexAligner>
  );
};
